import { request } from './api'
import {
  loadPersistentMap,
  mapCacheScope,
  peekPersistentMap,
  replacePersistentMap,
  retainPersistentMapScope,
  updatePersistentMap,
  type PersistentMapState,
} from './mapPersistentCache'
import type {
  JsonObject,
  MapChunk,
  MapMeta,
  MapOverview,
  MapRevisions,
  MapSnapshot,
} from './types'

export const MAP_WARM_FRESH_MS = 15_000

const confirmedAt = new Map<string, number>()
const warmups = new Map<string, Promise<PersistentMapState | null>>()

function chunkKey(x: number, y: number): string {
  return `${String(x)}:${String(y)}`
}

function sameBounds(left: MapMeta['activeBounds'], right: MapRevisions['activeBounds']): boolean {
  return (
    left.minX === right.minX &&
    left.maxX === right.maxX &&
    left.minY === right.minY &&
    left.maxY === right.maxY
  )
}

function groupSnapshotObjects(snapshot: MapSnapshot): MapChunk[] {
  const grouped = new Map<string, MapChunk>()
  const revisions = new Map(
    snapshot.chunkRevisions.map((item) => [chunkKey(item.chunkX, item.chunkY), item.revision]),
  )
  for (const item of snapshot.objects) {
    const cx = Math.floor(item.x / snapshot.meta.chunkSize)
    const cy = Math.floor(item.y / snapshot.meta.chunkSize)
    const key = chunkKey(cx, cy)
    let chunk = grouped.get(key)
    if (!chunk) {
      chunk = {
        worldId: snapshot.worldId,
        chunk: { x: cx, y: cy, size: snapshot.meta.chunkSize },
        mapRevision: snapshot.mapRevision,
        chunkRevision: revisions.get(key) ?? 0,
        baseTerrain: snapshot.meta.baseTerrain,
        objects: [],
        terrain: [],
      }
      grouped.set(key, chunk)
    }
    chunk.objects.push(item)
  }
  return Array.from(grouped.values())
}

function snapshotWorld(snapshot: MapSnapshot): Omit<PersistentMapState['world'], 'scope' | 'savedAtMs'> {
  return {
    meta: snapshot.meta,
    overview: snapshot.overview,
    markers: snapshot.markers,
    projectionRevision: snapshot.projectionRevision,
    markerRevision: snapshot.markerRevision,
    worldTimeUs: snapshot.worldTimeUs,
    chunkRevisions: snapshot.chunkRevisions,
    terrain: snapshot.terrain,
  }
}

async function fullSnapshot(scope: string, worldId: string): Promise<PersistentMapState | null> {
  const snapshot = await request<MapSnapshot>(
    `/api/v2/worlds/${worldId}/map/snapshot?resolution=64`,
    { cache: 'reload' },
  )
  const chunks = groupSnapshotObjects(snapshot)
  await replacePersistentMap(scope, snapshotWorld(snapshot), chunks)
  confirmedAt.set(scope, Date.now())
  return peekPersistentMap(scope)
}

function applyProtectionClock(chunks: MapChunk[], worldTimeUs: number): MapChunk[] {
  const changed: MapChunk[] = []
  for (const chunk of chunks) {
    let chunkChanged = false
    const objects = chunk.objects.map((item) => {
      const nextProtected =
        item.protectionUntilWorldTimeUs !== null &&
        item.protectionUntilWorldTimeUs > worldTimeUs
      if (nextProtected === item.protected) return item
      chunkChanged = true
      return { ...item, protected: nextProtected }
    })
    if (chunkChanged) changed.push({ ...chunk, objects })
  }
  return changed
}

async function synchronize(
  scope: string,
  worldId: string,
  local: PersistentMapState | null,
  allowInitialSnapshot: boolean,
): Promise<PersistentMapState | null> {
  if (!local) return allowInitialSnapshot ? fullSnapshot(scope, worldId) : null

  const revisions = await request<MapRevisions>(
    `/api/v2/worlds/${worldId}/map/revisions`,
    { cache: 'reload' },
  )
  if (
    revisions.projectionRevision !== local.world.projectionRevision ||
    revisions.chunkSize !== local.world.meta.chunkSize ||
    !sameBounds(local.world.meta.activeBounds, revisions.activeBounds)
  ) {
    return fullSnapshot(scope, worldId)
  }

  const localRevisions = new Map(
    local.world.chunkRevisions.map((item) => [chunkKey(item.chunkX, item.chunkY), item.revision]),
  )
  const changedRevisionRows = revisions.chunkRevisions.filter(
    (item) => localRevisions.get(chunkKey(item.chunkX, item.chunkY)) !== item.revision,
  )
  const changedChunks: MapChunk[] = []
  const concurrency = 3
  for (let index = 0; index < changedRevisionRows.length; index += concurrency) {
    const batch = changedRevisionRows.slice(index, index + concurrency)
    changedChunks.push(
      ...(await Promise.all(
        batch.map((item) =>
          request<MapChunk>(
            `/api/v2/worlds/${worldId}/map/chunks/${String(item.chunkX)}/${String(item.chunkY)}?terrain=0`,
            { cache: 'reload' },
          ),
        ),
      )),
    )
  }

  let overview: MapOverview = local.world.overview
  if (overview.mapRevision !== revisions.mapRevision) {
    overview = await request<MapOverview>(
      `/api/v2/worlds/${worldId}/map/overview?resolution=64`,
      { cache: 'reload' },
    )
  }

  let markers: JsonObject[] = local.world.markers
  if (revisions.markerRevision !== local.world.markerRevision) {
    const response = await request<{ items: JsonObject[] }>(
      `/api/v2/worlds/${worldId}/map/markers`,
      { cache: 'reload' },
    )
    markers = response.items
  }

  const protectionChunks = applyProtectionClock(local.chunks, revisions.worldTimeUs)
  const persistentWorld: Omit<PersistentMapState['world'], 'scope' | 'savedAtMs'> = {
    ...local.world,
    meta: { ...local.world.meta, mapRevision: revisions.mapRevision },
    overview,
    markers,
    projectionRevision: revisions.projectionRevision,
    markerRevision: revisions.markerRevision,
    worldTimeUs: revisions.worldTimeUs,
    chunkRevisions: revisions.chunkRevisions,
  }
  const changedByKey = new Map<string, MapChunk>()
  for (const chunk of [...protectionChunks, ...changedChunks]) {
    changedByKey.set(chunkKey(chunk.chunk.x, chunk.chunk.y), chunk)
  }
  await updatePersistentMap(scope, persistentWorld, Array.from(changedByKey.values()))
  confirmedAt.set(scope, Date.now())
  return peekPersistentMap(scope)
}

export function mapWarmConfirmedRecently(scope: string): boolean {
  const timestamp = confirmedAt.get(scope)
  return timestamp !== undefined && Date.now() - timestamp < MAP_WARM_FRESH_MS
}

export function currentMapWarmup(scope: string): Promise<PersistentMapState | null> | null {
  return warmups.get(scope) ?? null
}

export function prewarmMap(
  accountId: string,
  worldId: string,
  allowInitialSnapshot = false,
): Promise<PersistentMapState | null> {
  const scope = mapCacheScope(accountId, worldId)
  retainPersistentMapScope(scope)
  const running = warmups.get(scope)
  if (running) return running

  const promise = (async () => {
    const local = await loadPersistentMap(scope)
    if (mapWarmConfirmedRecently(scope)) return local
    try {
      return await synchronize(scope, worldId, local, allowInitialSnapshot)
    } catch {
      // Offline/temporary failures keep the local snapshot hot and usable.
      return peekPersistentMap(scope) ?? local
    }
  })().finally(() => {
    warmups.delete(scope)
  })
  warmups.set(scope, promise)
  return promise
}
